<style lang="scss" scoped>
.writeback-table {
	& ::v-deep .el-table__expanded-cell {
		padding: 0 !important;
	}
	&__refresh {
		margin-right: 10px;
	}
	&__payload {
		padding: 40px;
		background: $grey--100;
		&__payload__url {
			color: #606266;
			font-size: 14px;
			font-family: Monaco, Menlo, Consolas, Bitstream Vera Sans Mono, monospace;
		}
		.vjs-value {
			color: #4c85ff !important;
		}
	}
	&__pagination {
		margin-top: 20px;
	}
	.show-payload {
		text-align: center;
	}
	.in_circle {
		margin-right: 0;
	}
	.current-page {
		display: inline-block;
		width: 37px;
		border: 1px solid #d7d7d7;
		height: 29px;
		text-align: center;
		line-height: 26px;
		margin: 0px 5px;
		color: #797979;
		border-radius: 4px;
	}
}
</style>

<template lang="pug">
    el-card.writeback-table
        card-inside-header
            template(slot="left")
                h3 {{ pageTitle() }}
            template(slot="right")
                el-button(size="medium", icon="fe fe-refresh-cw icon-only", @click="refetch()").writeback-table__refresh
                el-date-picker(v-model='dateRange', type='datetimerange', align='right', unlink-panels='', range-separator='-', start-placeholder='Start date', end-placeholder='End date', :picker-options='pickerOptions')
        empty(v-if="!loading && !filteredWritebacks.length", :side-margin="false") There are no writebacks in this timeframe
        el-table(v-else, v-loading="loading", ref="writebackTable", :data="filteredWritebacks", @sort-change="handleSort" @expand-change="handleExpand")
            el-table-column(type="expand")
                template(slot-scope="props")
                    .writeback-table__payload
                        p.writeback-table__payload__url {{ props.row.url }}
                        json-pretty(:code="props.row.payload")
            el-table-column(label="ID", prop="id")
            el-table-column(v-if="showSchool", label="School", prop="School.name", :show-overflow-tooltip="true")
            el-table-column(label="Company", prop="Company.name", :show-overflow-tooltip="true")
            el-table-column(label="Status", prop="status", align="center", width=100, :filters="statusFilters", :filter-method="handleSortStatus", filter-multiple)
                slot(slot-scope="scope")
                    el-tag(size="small", :type="activityType(scope.row.status)") {{ scope.row.status | capitalise }}
            el-table-column(v-can:retry-writebacks, width=60)
                slot(slot-scope="scope")
                    el-popover(v-if="retryable(scope.row)", placement='right', width='400', trigger='click')
                        template
                            div(v-loading="scope.row.loading")
                                p By clicking retry we will attempt to send the payload again. Be careful as this payload could now be out of date. You can only retry the same writeback once an hour.
                                el-button(@click="retry(scope.row)",  size="mini") Retry
                        el-button(slot='reference', size="mini", circle, type="info")
                            i.fa.fa-refresh.in_circle
            el-table-column(label="Created", prop="created_at", align="center")
                slot(slot-scope="scope")
                    el-tooltip(placement='top')
                        div(slot='content') {{ scope.row.created_at | standard_datetime }}
                        span {{ scope.row.created_at | standard_datetime }}
            el-table-column(label="type", prop="type", align="center" :show-overflow-tooltip="true", :filters="typeFilters", :filter-method="handleSortType", filter-multiple)
                slot(slot-scope="scope")
                    | {{ scope.row.type | capitalise }}
            el-table-column(prop="url", label="Actions", align="center")
                template(slot-scope="scope")
                    el-button(type="text" @click="expandRow(scope.row)") Show payload
        div.pagination(v-if="Writebacks.data.length")
            el-button(:disabled="!Writebacks.paginator.prev_page_url", type="primary", size="mini", @click="$router.push({ query: { ...$route.query, page: Writebacks.paginator.current_page - 1 } })") prev
            div.current-page {{ Writebacks.paginator.current_page }}
            el-button(:disabled="!Writebacks.paginator.next_page_url", type="primary", size="mini", @click="$router.push({ query: { ...$route.query, page: Writebacks.paginator.current_page + 1 } })") next
</template>

<script>
import moment from "moment";
import gql from "graphql-tag";
import { startCase } from "lodash";
import JsonPretty from "@/components/JsonPretty.vue";
import WritebackQuery from "@/graphql/queries/Writebacks";

export default {
	name: "WritebackTable",

	components: {
		JsonPretty
	},

	watch: {
		dateRange: {
			deep: true,
			handler() {
				const start = moment.utc(this.dateRange[0]).unix();
				const end = moment.utc(this.dateRange[1]).unix();
				this.$router.push({ query: { ...this.$route.query, start, end } });
			}
		},
		$route: {
			handler: "refetch"
		}
	},

	props: {
		showSchool: {
			type: Boolean,
			required: true
		},
		startDate: {
			type: String,
			required: true
		},
		endDate: {
			type: String,
			required: true
		}
	},

	data() {
		let end = new Date();

		let start = moment
			.utc()
			.subtract(7, "days")
			.startOf("day");

		return {
			loading: true,
			Writebacks: {
				data: []
			},
			dateRange: [
				this.$route.query.start ? moment.unix(this.$route.query.start).toDate() : start,
				this.$route.query.end ? moment.unix(this.$route.query.end).toDate() : end
			],

			pickerOptions: {
				shortcuts: [
					{
						text: "Today",
						onClick(picker) {
							const end = moment.utc().endOf("day");
							const start = moment.utc().startOf("day");
							picker.$emit("pick", [start, end]);
						}
					},
					{
						text: "Yesterday",
						onClick(picker) {
							const end = moment
								.utc()
								.endOf("day")
								.subtract(1, "day");
							const start = moment
								.utc()
								.startOf("day")
								.subtract(1, "day");
							picker.$emit("pick", [start, end]);
						}
					},
					{
						text: "Last week",
						onClick(picker) {
							const end = moment.utc().endOf("day");
							const start = moment
								.utc()
								.subtract(1, "week")
								.startOf("day");
							picker.$emit("pick", [start, end]);
						}
					},
					{
						text: "Last month",
						onClick(picker) {
							const end = moment.utc().endOf("day");
							const start = moment
								.utc()
								.subtract(1, "month")
								.startOf("day");
							picker.$emit("pick", [start, end]);
						}
					}
				]
			}
		};
	},

	computed: {
		filteredWritebacks() {
			if (!this.Writebacks || !this.Writebacks.data.length) return [];

			if (this.$route.query.status) {
				return this.Writebacks.data.filter(w => w.status == this.$route.query.status);
			}

			return this.Writebacks.data;
		},

		statusFilters() {
			return this.Writebacks.data
				.map(wb => wb.status)
				.filter((v, i, a) => a.indexOf(v) === i)
				.map(status => ({ text: status, value: status }));
		},
		typeFilters() {
			return this.Writebacks.data
				.map(wb => wb.type)
				.filter((v, i, a) => a.indexOf(v) === i)
				.map(type => ({ text: type, value: type }));
		}
	},

	methods: {
		refetch() {
			this.loading = true;
			this.$apollo.queries.Writebacks.refetch();
		},
		handleSortStatus(value, row) {
			return row.status == value;
		},
		handleSortType(value, row) {
			return row.type == value;
		},
		activityType(type) {
			let tag = "info";
			switch (type) {
				case "pending":
					tag = "primary";
					break;
				case "success":
					tag = "success";
					break;
				case "failed":
					tag = "warning";
					break;
			}
			return tag;
		},
		expandRow(row) {
			this.$refs.writebackTable.toggleRowExpansion(row);
		},
		pageTitle() {
			const title = this.$route.query.type ? startCase(this.$route.query.type.replace(".", " ")) : "All";

			return `${title} Writebacks`;
		},
		handleSort({ prop, order }) {
			this.currentPage = 1;
			this.order = order == "ascending" ? "asc" : "desc";
			this.orderBy = prop;
		},
		handleCurrentChange(page) {
			this.$router.push({ query: { ...this.$route.query, page: page } });
		},
		retryable(row) {
			if (this.$localStore.get(`retried-writeback-${row.id}`, false)) {
				return false;
			}

			return row.status == "failed";
		},
		async retry(row) {
			try {
				row.loading = true;
				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation RetryWritebacks($input: [RetryWritebackInput]!) {
							RetryWritebacks(input: $input)
						}
					`,
					variables: {
						input: [{ writeback_id: row.id }]
					}
				});
				if (response) {
					this.$message.success("Writeback has been resent.");
					this.$localStore.set("retried-writeback-" + row.id, row.id, 1000 * 60 * 60);
					row.status = "pending";
				} else {
					this.$message.error("Failed to retry writeback.");
				}
				row.loading = false;
			} catch (error) {
				this.$message.error("Failed to retry writeback.");
			}
		},
	},

	apollo: {
		Writebacks: {
			query: WritebackQuery,
			variables() {
				return {
					cursor: this.$route.query.page || 1,
					school: this.$route.params.school,
					type: this.$route.query.type,
					startDate: this.startDate,
					endDate: this.endDate,
					limit: 50,
					company: this.$route.query.company_id,
					status: this.$route.query.status
				};
			},
			result() {
				this.loading = false;
			},
			error() {
				this.$message.error("Error loading data. Please try a different date range");
			},
			fetchPolicy: "no-cache"
		}
	}
};
</script>
